/**
 * useCredits — reactive subscription to the user's credit balance + plan.
 *
 * Source of truth: POST /api/cl/credits/me (server-derived from the
 * subscriptions + credits_balance collections). We refetch on:
 *   - mount + auth change
 *   - PocketBase realtime ping on `credits_balance` rows for this user
 *     (credit consumption from generation jobs flips a row's `used`)
 *   - PocketBase realtime ping on `subscriptions` rows for this user
 *     (LS webhook flips plan / status / period — happens out-of-band
 *     and the dropdown / pill / Settings card all need to react live)
 *   - explicit window event "cl:credits-refresh" (fired by callers
 *     after they enqueue a generation, to keep the pill up-to-date
 *     without waiting for the realtime push to round-trip).
 *
 * Realtime gotchas (learned from the v2.0.0 hotfix):
 *   - subscribe ONLY when authenticated. PB realtime carries the auth
 *     token in the EventSource URL; subscribing before auth produces
 *     "ClientResponseError 403" on every auth flip.
 *   - debounce the refetch — a webhook event can flip a sub row AND the
 *     credits_balance row in the same tick, and we don't want two
 *     refetches.
 *   - tear the subscription down on logout / unmount before the next
 *     auth attempt subscribes; otherwise PB sees the previous token in
 *     the unsubscribe call and 403s.
 */

import { useEffect, useState, useCallback, useRef } from "react";
import { fetchCredits } from "@/lib/billing";
import { useAuth } from "@/hooks/useAuth";
import type { CreditsAggregate } from "@/types";

export interface UseCreditsResult {
  loading: boolean;
  data: CreditsAggregate | null;
  refresh: () => Promise<void>;
  error: string | null;
}

export function useCredits(): UseCreditsResult {
  const { isAuthed, user } = useAuth();
  const [data, setData]       = useState<CreditsAggregate | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError]     = useState<string | null>(null);
  const mounted = useRef(true);

  useEffect(() => () => { mounted.current = false; }, []);

  const refresh = useCallback(async () => {
    if (!isAuthed) { setData(null); return; }
    try {
      setLoading(true);
      setError(null);
      const result = await fetchCredits();
      if (mounted.current) setData(result);
    } catch (e) {
      if (mounted.current) setError(e instanceof Error ? e.message : "Failed to load credits");
    } finally {
      if (mounted.current) setLoading(false);
    }
  }, [isAuthed]);

  // Initial + auth-change fetch
  useEffect(() => {
    if (!isAuthed) { setData(null); return; }
    void refresh();
  }, [isAuthed, refresh]);

  // ── Live updates ──
  // Previously a PocketBase SSE subscription on the user's
  // `subscriptions` + `credits_balance` rows. PB was decommissioned in
  // the v2.1 Django cutover and Django doesn't expose PB's realtime
  // endpoint, so the live push is gone. The pill/dropdown still stay
  // correct via: mount + auth-change fetch (above) and the
  // "cl:credits-refresh" window event (below) — generation callers fire
  // `bumpCredits()` right after enqueuing, which is the case that
  // actually matters for snappiness. Out-of-band LS plan changes reflect
  // on the next refresh/navigation. Re-adding a live push over Django
  // Channels is a Phase 2 follow-up.

  // Global event hook — generation callers fire this to nudge a refresh
  // without waiting for the realtime round-trip. Keeps the pill snappy.
  useEffect(() => {
    const handler = () => { void refresh(); };
    window.addEventListener("cl:credits-refresh", handler);
    return () => window.removeEventListener("cl:credits-refresh", handler);
  }, [refresh]);

  return { loading, data, refresh, error };
}

/** Convenience helper to fire a manual refresh from anywhere. */
export function bumpCredits() {
  if (typeof window !== "undefined") {
    window.dispatchEvent(new Event("cl:credits-refresh"));
  }
}
